import { IPersistable, PersistenceService, PersistMode } from '@barthauer/abc/core/data';
import { DocumentKind, MtDocument, Reminder } from '../models/mtData.model';
import { CompareHelper, ConvertHelper, DataUrl, FileHelper, GUID, MimeTypeEnum } from '@barthauer/abc/core';
import { MTHelper } from '../shared/helpers';

export class MobileDocumentViewModel implements IPersistable {
  private _hasPersistedDataObject: boolean;
  private _dateCache: Date = null;
  private _previewImageDataUrl: DataUrl = null;
  private _mtDocument: MtDocument;
  private persistenceService: PersistenceService;
  private fileExtension: string;
  private mimeType: MimeTypeEnum;
  public file: File;

  get mtDocument(): MtDocument {
    return this._mtDocument;
  }
  get id(): string {
    return this._mtDocument.Id;
  }
  get objectType(): string {
    return 'Document';
  }
  get insertAt(): Date {
    return this._mtDocument.InsertAt;
  }
  persistenceCategory?: string;
  get isPersisted(): boolean {
    return this._hasPersistedDataObject;
  }
  get designation(): string {
    return this._mtDocument.Designation;
  }

  set designation(value: string) {
    if (this._mtDocument.Designation !== value) {
      this.persistenceService.addObjectValue(this, 'Designation', value, this._mtDocument.Designation);
      this._mtDocument.Designation = value;
    }
  }

  get date(): Date {
    if (!this._dateCache) {
      if (this._mtDocument.Date) {
        this._dateCache = new Date(this._mtDocument.Date);
      }
    }
    return this._dateCache;
  }

  set date(value: Date) {
    const d: string = ConvertHelper.toISODateString(value);
    this._dateCache = null;
    if (this._mtDocument.Date !== d) {
      this.persistenceService.addObjectValue(this, 'Date', d, this._mtDocument.Date);
      this._mtDocument.Date = d;
    }
  }

  get kind(): string {
    return this._mtDocument.Kind;
  }

  set kind(value: string) {
    if (this._mtDocument.Kind !== value) {
      this.persistenceService.addObjectValue(this, 'Kind', value, this._mtDocument.Kind);
      this._mtDocument.Kind = value;
    }
  }

  get object(): string {
    return this._mtDocument.Object;
  }

  set object(value: string) {
    if (this._mtDocument.Object !== value) {
      this.persistenceService.addObjectValue(this, 'Object', value, this._mtDocument.Object);
      this._mtDocument.Object = value;
    }
  }

  //previewImageDataUrl not under control of persistenceService
  get previewImageDataUrl(): DataUrl {
    if (!this._previewImageDataUrl && this.object) {
      this._previewImageDataUrl = new DataUrl(
        this.object,
        this.mimeType,
        this.fileExtension ?? FileHelper.getExtension(this.designation),
      );
    }
    return this._previewImageDataUrl;
  }

  set previewImageDataUrl(value: DataUrl) {
    this._previewImageDataUrl = value;
  }

  constructor() {}

  public load(
    persistenceService: PersistenceService,
    mtDocument: MtDocument,

    persistenceCategory: string,
    hasPersistedDataObject: boolean,
  ) {
    this.persistenceService = persistenceService;
    this._mtDocument = mtDocument;
    this.persistenceCategory = persistenceCategory;
    this._hasPersistedDataObject = hasPersistedDataObject;
  }

  public createNewByDataUrl(
    persistenceService: PersistenceService,
    persistenceCategory: string,
    parentObject: IPersistable,
    designation: string,
    date: Date,
    dataUrl: DataUrl,
  ): MtDocument {
    return this.createNew(
      persistenceService,
      persistenceCategory,
      parentObject,
      designation,
      DocumentKind.Bild,
      date,
      dataUrl.base64,
      dataUrl.fileExtension,
      dataUrl.mimeType,
    );
  }

  public createNewByFile(
    persistenceService: PersistenceService,
    persistenceCategory: string,
    parentObject: IPersistable,
    designation: string,
    date: Date,
    file: File,
  ): MtDocument {
    return this.createNew(
      persistenceService,
      persistenceCategory,
      parentObject,
      designation,
      DocumentKind.Datei,
      date,
      undefined,
      undefined,
      undefined,
      file,
    );
  }

  private createNew(
    persistenceService: PersistenceService,
    persistenceCategory: string,
    parentObject: IPersistable,
    designation: string,
    kind: string,
    date: Date,
    object?: string,
    fileExtension?: string,
    mimeType?: MimeTypeEnum,
    file?: File,
  ): MtDocument {
    this.fileExtension = fileExtension;
    this.mimeType = mimeType;
    this.file = file;

    const newDoc: MtDocument = new MtDocument({
      Id: GUID.newGUID(),
      Designation: designation,
      Kind: kind,
      Date: ConvertHelper.toISODateString(date),
      Object: object,
      InsertAt: date, //will not be persisted, for sorting only
    });

    this.load(persistenceService, newDoc, persistenceCategory, false);

    this.persistenceService.addObjectForMerge(this);
    this.persistenceService.addObjectValue(this, 'ParentId', parentObject.id);
    this.persistenceService.addObjectValue(this, 'ParentObjectType', parentObject.objectType);
    this.persistenceService.addObjectValue(this, 'Designation', designation);
    this.persistenceService.addObjectValue(this, 'Kind', kind);
    this.persistenceService.addObjectValue(this, 'Date', newDoc.Date);
    if (object) {
      this.persistenceService.addObjectValue(this, 'Object', object);
    }

    return newDoc;
  }

  public readFile: (file: File) => Promise<string>;
  public afterPersisted: () => void;

  public async beforePersisting() {
    if (this.file) {
      this.object = await this.readFile(this.file);
    }
  }

  public async changesPersisted(persistMode: PersistMode) {
    if (persistMode === PersistMode.Insert || persistMode === PersistMode.Update || persistMode === PersistMode.Merge) {
      this._hasPersistedDataObject = true;
      if (this.file) {
        //file: remove object to have less memory consumption after save
        this.mtDocument.Object = null;
      } else {
        //image: decrease image size to have less memory consumption after save
        this.mtDocument.Object = (
          await MTHelper.resizeImage(new DataUrl(this.object, this.mimeType, this.fileExtension))
        ).base64;
      }
      this.previewImageDataUrl = null;
    } else {
      this._hasPersistedDataObject = false;
    }
    if (this.afterPersisted) {
      this.afterPersisted();
    }
  }

  public revertChanges?() {
    if (this._hasPersistedDataObject) {
      const pack = this.persistenceService.getPendingObjectPackage(this);
      if (pack && !CompareHelper.isEmptyObject(pack.fieldValueHistories)) {
        pack.setOriginalFieldHistoryValue('Designation', (value) => (this._mtDocument.Designation = value));
        pack.setOriginalFieldHistoryValue('Kind', (value) => (this._mtDocument.Kind = value));
        pack.setOriginalFieldHistoryValue('Date', (value) => (this._mtDocument.Date = value));
        pack.setOriginalFieldHistoryValue('Object', (value) => (this._mtDocument.Object = value));
      }
    } else {
      this.persistenceService.addObjectForDelete(this);
    }
  }
}

export class MobileReminderViewModel implements IPersistable {
  private _reminder: Reminder;
  private persistenceService: PersistenceService;
  persistenceCategory?: string;

  get id(): string {
    return this._reminder.Id;
  }
  get objectType(): string {
    return 'Reminder';
  }
  get isActive(): boolean {
    return this._reminder.IsActive;
  }

  constructor() {}

  public load(persistenceService: PersistenceService, reminder: Reminder, persistenceCategory: string) {
    this.persistenceService = persistenceService;
    this._reminder = reminder;
    this.persistenceCategory = persistenceCategory;
  }

  public createNew(
    persistenceService: PersistenceService,
    persistenceCategory: string,
    parentObject: IPersistable,
    isActive: boolean,
  ): Reminder {
    const newReminder: Reminder = new Reminder({
      Id: GUID.newGUID(),
      isActive: isActive,
    });

    this.load(persistenceService, newReminder, persistenceCategory);

    this.persistenceService.addObjectForInsert(this);
    this.persistenceService.addObjectValue(this, 'ParentId', parentObject.id);
    this.persistenceService.addObjectValue(this, 'ParentObjectType', parentObject.objectType);
    this.persistenceService.addObjectValue(this, 'IsActive', isActive);

    return newReminder;
  }
}
